import { useEffect } from 'preact/hooks';

import { useDangerousLatestValueRef } from '@/hooks/useLatestValueRef';

import { ModalConfig } from '../types';
import FeedbackModal from './ui/FeedbackModal';
import TourVideoModal from './ui/TourVideoModal';
import WelcomeModal from './ui/WelcomeModal';

const MASCOT_SRC = 'https://zoolife.tv/assets/guidedTour/mascot/Talking_cropped.png';
const MAP_SRC = 'https://zoolife.tv/assets/guidedTour/guided-tour-map-bg-transparent.png';

interface TourModalSlotProps {
  modal: ModalConfig;
  advanceTo: (stepId: string) => void;
}

/**
 * Renders the step's full-screen modal. These use grommet <Layer>, which portals to the body,
 * so mounting inside the runner still overlays the whole page. Both transitions come from
 * config (`nextStepId` / `onCloseStepId`) rather than hardcoded step ids.
 */
const TourModalSlot = ({ modal, advanceTo }: TourModalSlotProps) => {
  const advanceToRef = useDangerousLatestValueRef(advanceTo);
  const onClose = () => advanceTo(modal.onCloseStepId ?? modal.nextStepId ?? '');

  // A video step with no source would trap the user (banner stays hidden while modal is
  // "resolved"); fire the close transition so the tour can advance.
  useEffect(() => {
    if (modal.kind !== 'video' || modal.src) return;
    const fallback = modal.onCloseStepId ?? modal.nextStepId;
    if (fallback) advanceToRef.current(fallback);
  }, [modal.kind, modal.src, modal.onCloseStepId, modal.nextStepId, advanceToRef]);

  if (modal.kind === 'welcome') {
    return (
      <WelcomeModal
        onStart={() => modal.nextStepId && advanceTo(modal.nextStepId)}
        mascotSrc={MASCOT_SRC}
        mapSrc={MAP_SRC}
      />
    );
  }

  if (modal.kind === 'video') {
    if (!modal.src) return null;
    return <TourVideoModal src={modal.src} onClose={onClose} />;
  }

  return (
    <FeedbackModal
      isSubmitted={Boolean(modal.submitted)}
      onRatingSubmit={() => modal.nextStepId && advanceTo(modal.nextStepId)}
      onFeedbackComplete={onClose}
    />
  );
};

export default TourModalSlot;
